<div class="container-fluid h-100 px-4 px-md-5">
  <div class="my-4">

    <div class="page-header">
      <button class="back-btn" (click)="goBack()" type="button">← Back</button>
      <h1>Tracing</h1>
      <span *ngIf="project" class="project-name-badge">{{ project.name }}</span>
    </div>

    <div *ngIf="loading" class="dashboard-loading">
      <div class="dashboard-spinner"></div>
    </div>

    <div *ngIf="!loading && error" class="state-msg state-msg--error">Failed to load tracing data.</div>

    <ng-container *ngIf="!loading && !error">

      <div class="stats-row" *ngIf="stats">
        <div class="stat-card">
          <span class="stat-card__label">Requests (24h)</span>
          <span class="stat-card__value">{{ stats.sampled ? '≈ ' : '' }}{{ stats.transactions_24h | number }}</span>
          <span class="stat-card__sub" *ngIf="stats.sampled">estimated from {{ stats.recorded_24h | number }} traced</span>
        </div>
        <div class="stat-card">
          <span class="stat-card__label">p50</span>
          <span class="stat-card__value">{{ formatDuration(stats.p50_ms) }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-card__label">p95</span>
          <span class="stat-card__value">{{ formatDuration(stats.p95_ms) }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-card__label">p99</span>
          <span class="stat-card__value">{{ formatDuration(stats.p99_ms) }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-card__label">Failed</span>
          <span class="stat-card__value" [class.stat-card__value--bad]="stats.error_rate > 0">
            {{ stats.error_rate * 100 | number:'1.0-2' }}%
          </span>
          <span class="stat-card__sub">{{ stats.failed_24h | number }} of {{ stats.sampled ? '≈ ' : '' }}{{ stats.transactions_24h | number }}</span>
        </div>
      </div>

      <div *ngIf="stats && stats.transactions_24h === 0" class="empty-state">
        <p class="mb-2"><strong>No traces yet.</strong></p>
        <p class="text-muted mb-0">
          Tracing is off until you set <code>traces_sample_rate</code> in the SDK — see
          <a [routerLink]="['/main', 'project', projectId, 'errors']">Errors → Setup</a>.
        </p>
      </div>

      <!-- Slowest endpoints ---------------------------------------------- -->
      <section class="panel" *ngIf="stats?.slowest?.length">
        <h2 class="panel__title">Slowest endpoints (24h)</h2>
        <div class="table-scroll">
        <table class="table table-sm slow-table stack-table">
          <thead>
            <tr>
              <th>Transaction</th>
              <th class="text-end">Requests</th>
              <th class="text-end">Avg</th>
              <th class="text-end">DB time</th>
              <th class="text-end">Failed</th>
              <th class="bar-column"></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let slow of stats.slowest" (click)="filterByName(slow)" class="slow-table__row">
              <td class="mono stack-table__title">{{ slow.name }}</td>
              <td class="text-end" data-label="Requests">{{ slow.count | number }}</td>
              <td class="text-end" data-label="Avg">{{ formatDuration(slow.avg_ms) }}</td>
              <td class="text-end text-muted" data-label="DB">{{ formatDuration(slow.db_ms) }}</td>
              <td class="text-end" data-label="Failed" [class.text-danger]="slow.failures > 0">{{ slow.failures }}</td>
              <td class="bar-column stack-table__bar">
                <div class="bar"><div class="bar__fill" [style.width.%]="slowShare(slow)"></div></div>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </section>

      <!-- Filters -------------------------------------------------------- -->
      <div class="filter-bar">
        <div class="segmented">
          <button type="button" class="segmented__btn" [class.segmented__btn--active]="filters.status === ''"
            (click)="updateFilter({ status: '' })">All</button>
          <button type="button" class="segmented__btn" [class.segmented__btn--active]="filters.status === 'ok'"
            (click)="updateFilter({ status: 'ok' })">OK</button>
          <button type="button" class="segmented__btn" [class.segmented__btn--active]="filters.status === 'error'"
            (click)="updateFilter({ status: 'error' })">Failed</button>
        </div>

        <input class="form-control form-control-sm filter-bar__search" type="search"
          placeholder="Search transaction, URL or user…" [value]="filters.q"
          (change)="updateFilter({ q: $any($event.target).value })" />

        <input class="form-control form-control-sm filter-bar__number" type="number" min="0" step="100"
          placeholder="Slower than (ms)" [value]="filters.min_duration_ms"
          (change)="updateFilter({ min_duration_ms: $any($event.target).value })" />

        <select class="form-select form-select-sm filter-bar__select" *ngIf="stats?.environments?.length"
          [value]="filters.environment" (change)="updateFilter({ environment: $any($event.target).value })">
          <option value="">All environments</option>
          <option *ngFor="let env of stats.environments" [value]="env">{{ env }}</option>
        </select>

        <select class="form-select form-select-sm filter-bar__select" [value]="filters.sort"
          (change)="updateFilter({ sort: $any($event.target).value })">
          <option value="recent">Most recent</option>
          <option value="duration">Slowest</option>
        </select>

        <span class="active-filter" *ngIf="filters.name">
          {{ filters.name }}
          <button type="button" class="active-filter__clear" (click)="clearNameFilter()">×</button>
        </span>
      </div>

      <div *ngIf="loadingTransactions" class="state-msg">Loading traces…</div>

      <div *ngIf="!loadingTransactions && transactions.length === 0 && stats?.transactions_24h"
        class="state-msg">No transactions match these filters.</div>

      <div class="table-scroll" *ngIf="!loadingTransactions && transactions.length > 0">
      <table class="table table-hover traces-table stack-table">
        <thead>
          <tr>
            <th>Transaction</th>
            <th>User</th>
            <th class="text-end">DB</th>
            <th class="text-end">Duration</th>
            <th class="text-end">When</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let transaction of transactions" (click)="openTransaction(transaction)"
            class="traces-table__row">
            <td class="stack-table__title">
              <div class="trace-title">
                <span class="method-badge">{{ transaction.http_method || transaction.op }}</span>
                <span class="mono">{{ transaction.name }}</span>
                <span class="status-badge status-badge--error" *ngIf="transaction.status === 'error'">failed</span>
                <span class="code-badge" *ngIf="transaction.status_code">{{ transaction.status_code }}</span>
              </div>
              <div class="bar bar--inline">
                <div class="bar__fill" [class.bar__fill--bad]="transaction.status === 'error'"
                  [style.width.%]="durationShare(transaction.duration_ms)"></div>
              </div>
            </td>
            <td class="text-muted" data-label="User">{{ describeUser(transaction) }}</td>
            <td class="text-end text-muted" data-label="DB">
              {{ transaction.db_span_count }}q · {{ formatDuration(transaction.db_time_ms) }}
            </td>
            <td class="text-end duration-cell" data-label="Duration">{{ formatDuration(transaction.duration_ms) }}</td>
            <td class="text-end text-muted" data-label="When">{{ relativeTime(transaction.start_timestamp) }}</td>
          </tr>
        </tbody>
      </table>
      </div>

      <div class="pagination-bar" *ngIf="!loadingTransactions && totalPages > 1">
        <button class="pw-btn pw-btn--ghost" type="button" [disabled]="currentPage <= 1"
          (click)="goToPage(currentPage - 1)">Previous</button>
        <span class="text-muted">
          Page {{ currentPage }} of {{ totalPages }} · {{ transactionCount | number }} transactions
        </span>
        <button class="pw-btn pw-btn--ghost" type="button" [disabled]="currentPage >= totalPages"
          (click)="goToPage(currentPage + 1)">Next</button>
      </div>

    </ng-container>

  </div>
</div>
